import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Flex, Heading, Text, Icon, Box, SimpleGrid, Badge, useColorModeValue,
  Spinner, Table, Thead, Tbody, Tr, Th, Td,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, Textarea, HStack, VStack, useToast, useDisclosure, IconButton, Tooltip,
  InputGroup, InputLeftElement, Input, Divider, Grid, GridItem,
  Tabs, TabList, Tab, TabPanels, TabPanel,
} from '@chakra-ui/react';
import {
  MdVerifiedUser, MdCheckCircle, MdCancel, MdHourglassEmpty,
  MdSearch, MdThumbUp, MdThumbDown, MdVisibility, MdArrowUpward, MdArrowDownward,
  MdLocationOn, MdAssignment,
} from 'react-icons/md';
import Card from 'components/card/Card.js';
import TesteDeFumacaForm from 'components/forms/TesteDeFumacaForm';
import TesteCoranteForm  from 'components/forms/TesteCoranteForm';
import { db } from '../../../firebase';
import {
  collection, onSnapshot, updateDoc, doc, getDoc,
  query, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../auth/AuthProvider';

// ─── constants ────────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  rascunho:             'gray',
  enviada:              'blue',
  aguardando_validacao: 'orange',
  aprovada:             'green',
  reprovada:            'red',
};
const STATUS_LABELS = {
  rascunho:             'Rascunho',
  enviada:              'Enviada',
  aguardando_validacao: 'Ag. Validação',
  aprovada:             'Aprovada',
  reprovada:            'Reprovada',
};

function fmt(ts) {
  if (!ts) return '--';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}
function fmtDate(ts) {
  if (!ts) return '--';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('pt-BR');
}

// ─── Detalhe de um campo ──────────────────────────────────────────────────────
function Campo({ label, value, span = 1 }) {
  const lbl  = useColorModeValue('gray.500', 'gray.400');
  const txt  = useColorModeValue('navy.700', 'white');
  return (
    <GridItem colSpan={span}>
      <Text fontSize="10px" fontWeight="600" color={lbl} textTransform="uppercase" mb="1px">{label}</Text>
      <Text fontSize="13px" color={txt} fontWeight="500">{value || '--'}</Text>
    </GridItem>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function Validacoes() {
  const { user } = useAuth();
  const toast    = useToast();

  const textColor  = useColorModeValue('navy.700', 'white');
  const subText    = useColorModeValue('gray.500', 'gray.400');
  const cardBg     = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.100', 'gray.600');
  const theadBg    = useColorModeValue('gray.50', 'navy.900');
  const rowHover   = useColorModeValue('gray.50', 'navy.750');

  // ── state ──────────────────────────────────────────────────────────────────
  const [ordens,    setOrdens]    = useState([]);
  const [nomesMap,  setNomesMap]  = useState({});
  const [loading,   setLoading]   = useState(true);
  const [busca,     setBusca]     = useState('');
  const [sortField, setSortField] = useState('criadaEm');
  const [sortDir,   setSortDir]   = useState('desc');

  const [selectedOS,  setSelectedOS]  = useState(null);
  const [comentario,  setComentario]  = useState('');
  const [saving,      setSaving]      = useState(false);
  const { isOpen, onOpen, onClose }   = useDisclosure();

  const usersCache = useRef({});

  // ── real-time subscription ─────────────────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, 'ordens'), orderBy('criadaEm', 'desc'));
    const unsub = onSnapshot(q, async (snap) => {
      const docs = snap.docs.map((d) => {
        const data = d.data();
        const lat = data.lat ?? data.dadosEspecificos?.lat ?? null;
        const lng = data.lng ?? data.dadosEspecificos?.lng ?? null;
        return { id: d.id, ...data, ...(lat != null ? { lat, lng } : {}) };
      });
      setOrdens(docs);
      setLoading(false);

      // resolve technician names
      const uids = [...new Set(docs.map((o) => o.tecnicoUid).filter(Boolean))];
      const novos = {};
      await Promise.all(uids.map(async (uid) => {
        if (usersCache.current[uid]) { novos[uid] = usersCache.current[uid]; return; }
        try {
          const s = await getDoc(doc(db, 'users', uid));
          if (s.exists()) {
            const d = s.data();
            const n = d.firstName && d.lastName
              ? `${d.firstName} ${d.lastName}`
              : (d.displayName && !d.displayName.includes('@')) ? d.displayName : d.email || '';
            usersCache.current[uid] = n; novos[uid] = n;
          }
        } catch { novos[uid] = ''; }
      }));
      setNomesMap((prev) => ({ ...prev, ...novos }));
    }, () => setLoading(false));
    return unsub;
  }, []);

  // ── stats ──────────────────────────────────────────────────────────────────
  const pendentes  = useMemo(() => ordens.filter((o) => o.status === 'aguardando_validacao').length, [ordens]);
  const aprovadas  = useMemo(() => ordens.filter((o) => o.status === 'aprovada').length,             [ordens]);
  const reprovadas = useMemo(() => ordens.filter((o) => o.status === 'reprovada').length,            [ordens]);

  const STATS = [
    { label: 'Aguardando',  icon: MdHourglassEmpty, color: 'orange.400', value: pendentes  },
    { label: 'Aprovadas',   icon: MdCheckCircle,    color: 'green.400',  value: aprovadas  },
    { label: 'Reprovadas',  icon: MdCancel,         color: 'red.400',    value: reprovadas },
  ];

  // ── filtered + sorted OS aguardando ───────────────────────────────────────
  const ordensAguardando = useMemo(() => {
    let arr = ordens.filter((o) => o.status === 'aguardando_validacao');
    if (busca.trim()) {
      const b = busca.toLowerCase();
      arr = arr.filter((o) => {
        const nome = (nomesMap[o.tecnicoUid] || o.tecnicoNome || '').toLowerCase();
        return nome.includes(b)
          || (o.numeroOS    || '').toLowerCase().includes(b)
          || (o.tipoServico || '').toLowerCase().includes(b)
          || (o.cidade      || '').toLowerCase().includes(b)
          || (o.dadosEspecificos?.cidade || '').toLowerCase().includes(b);
      });
    }
    arr.sort((a, b) => {
      let va = a[sortField], vb = b[sortField];
      if (va && va.toDate) va = va.toDate().getTime();
      if (vb && vb.toDate) vb = vb.toDate().getTime();
      if (va == null) return 1; if (vb == null) return -1;
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [ordens, busca, nomesMap, sortField, sortDir]);

  function toggleSort(field) {
    if (sortField === field) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  }
  function SortIcon({ field }) {
    if (sortField !== field) return null;
    return <Icon as={sortDir === 'asc' ? MdArrowUpward : MdArrowDownward} ml="4px" boxSize="12px" />;
  }

  // ── open modal ─────────────────────────────────────────────────────────────
  function abrirOS(os) {
    setSelectedOS(os);
    setComentario('');
    onOpen();
  }

  // ── aprovar / reprovar ─────────────────────────────────────────────────────
  async function validar(novoStatus) {
    if (!selectedOS) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'ordens', selectedOS.id), {
        status:              novoStatus,
        comentarioValidacao: comentario.trim() || null,
        validadoPor:         user?.uid || null,
        validadoNome:        user?.displayName || user?.email || null,
        validadoEm:          serverTimestamp(),
        atualizadaEm:        serverTimestamp(),
      });
      toast({
        title: novoStatus === 'aprovada' ? '✅ OS Aprovada!' : '❌ OS Reprovada',
        description: `OS ${selectedOS.numeroOS || selectedOS.id.slice(-6)} atualizada.`,
        status: novoStatus === 'aprovada' ? 'success' : 'warning',
        duration: 3000,
      });
      onClose();
    } catch (e) {
      toast({ title: 'Erro ao validar OS', description: e.message, status: 'error', duration: 4000 });
    } finally {
      setSaving(false);
    }
  }

  // ── helpers ────────────────────────────────────────────────────────────────
  const de = selectedOS?.dadosEspecificos || {};
  const tecnicoNome = (os) => nomesMap[os?.tecnicoUid] || os?.tecnicoNome || '--';
  const cidadeOS    = (os) => os?.dadosEspecificos?.cidade || os?.cidade || '--';

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <Flex direction="column" pt={{ base: '8px', md: '16px', xl: '0px' }}>

      {/* Header ── */}
      <Card p="6" mb="6">
        <Flex align="center" gap="12px">
          <Icon as={MdVerifiedUser} boxSize="30px" color="brand.500" />
          <Box>
            <Heading size="md" color={textColor}>Validações</Heading>
            <Text fontSize="sm" color={subText}>
              Aprove ou reprove OS enviadas pelos técnicos
            </Text>
          </Box>
        </Flex>
      </Card>

      {/* Stats ── */}
      <SimpleGrid columns={{ base: 1, md: 3 }} gap="20px" mb="24px">
        {STATS.map(({ label, icon, color, value }) => (
          <Card key={label} p="6">
            <Flex align="center" gap="12px">
              <Icon as={icon} boxSize="28px" color={color} />
              <Box>
                <Text fontSize="2xl" fontWeight="bold" color={textColor}>{value}</Text>
                <Text fontSize="sm" color={subText}>{label}</Text>
              </Box>
            </Flex>
          </Card>
        ))}
      </SimpleGrid>

      {/* Tabela ── */}
      <Card bg={cardBg} p={{ base: '14px', md: '20px' }}>
        <Flex justify="space-between" align="center" mb="14px" wrap="wrap" gap="8px">
          <Heading size="sm" color={textColor}>
            OS Aguardando Validação
            {!loading && (
              <Badge ml="2" colorScheme="orange" fontSize="11px" borderRadius="full" px="2">
                {ordensAguardando.length}
              </Badge>
            )}
          </Heading>
          <InputGroup size="sm" maxW="220px">
            <InputLeftElement pointerEvents="none">
              <Icon as={MdSearch} color={subText} />
            </InputLeftElement>
            <Input
              placeholder="Buscar OS..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              borderRadius="8px"
            />
          </InputGroup>
        </Flex>

        {loading ? (
          <Flex justify="center" py="12"><Spinner size="lg" color="brand.500" /></Flex>
        ) : ordensAguardando.length === 0 ? (
          <Flex direction="column" align="center" py="12" gap="3">
            <Icon as={MdCheckCircle} boxSize="48px" color="green.300" />
            <Text color={subText} fontSize="sm">Nenhuma OS aguardando validação 🎉</Text>
          </Flex>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead bg={theadBg}>
                <Tr>
                  {[
                    { label: 'Nº OS',    field: 'numeroOS'    },
                    { label: 'Técnico',  field: 'tecnicoNome' },
                    { label: 'Tipo',     field: 'tipoServico', hide: true },
                    { label: 'Cidade',   field: 'cidade',      hide: true },
                    { label: 'Data',     field: 'criadaEm'    },
                  ].map(({ label, field, hide }) => (
                    <Th key={field} borderColor={borderColor} color={subText}
                      cursor="pointer" userSelect="none"
                      display={hide ? { base: 'none', md: 'table-cell' } : undefined}
                      onClick={() => toggleSort(field)}
                      _hover={{ color: 'brand.500' }} transition="color .15s">
                      <Flex align="center">{label}<SortIcon field={field} /></Flex>
                    </Th>
                  ))}
                  <Th borderColor={borderColor} />
                </Tr>
              </Thead>
              <Tbody>
                {ordensAguardando.map((os) => (
                  <Tr key={os.id} _hover={{ bg: rowHover }} transition="background .15s">
                    <Td borderColor={borderColor} fontWeight="600" fontSize="13px" color={textColor}>
                      {os.numeroOS || <Text as="span" color={subText} fontSize="11px">{os.id.slice(-6)}</Text>}
                    </Td>
                    <Td borderColor={borderColor} fontSize="13px">{tecnicoNome(os)}</Td>
                    <Td borderColor={borderColor} fontSize="12px"
                      display={{ base: 'none', md: 'table-cell' }}
                      maxW="160px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                      {os.tipoServico || '--'}
                    </Td>
                    <Td borderColor={borderColor} fontSize="12px"
                      display={{ base: 'none', md: 'table-cell' }}>
                      {cidadeOS(os)}
                    </Td>
                    <Td borderColor={borderColor} fontSize="11px" color={subText}>
                      {fmtDate(os.criadaEm)}
                    </Td>
                    <Td borderColor={borderColor} isNumeric>
                      <HStack spacing="1px" justify="flex-end">
                        <Tooltip label={os.lat && os.lng ? `GPS: ${Number(os.lat).toFixed(5)}, ${Number(os.lng).toFixed(5)}` : 'Sem GPS'} placement="top">
                          <span>
                            <Icon as={MdLocationOn} boxSize="14px"
                              color={os.lat && os.lng ? 'green.400' : 'gray.300'} />
                          </span>
                        </Tooltip>
                        <Tooltip label="Analisar OS" placement="top">
                          <IconButton
                            icon={<Icon as={MdVisibility} />}
                            size="xs" variant="ghost" colorScheme="brand"
                            aria-label="Analisar OS"
                            onClick={() => abrirOS(os)}
                          />
                        </Tooltip>
                        <Tooltip label="Aprovar" placement="top">
                          <IconButton
                            icon={<Icon as={MdThumbUp} />}
                            size="xs" variant="ghost" colorScheme="green"
                            aria-label="Aprovar"
                            onClick={() => { setSelectedOS(os); setComentario(''); validarDireto(os, 'aprovada'); }}
                          />
                        </Tooltip>
                        <Tooltip label="Reprovar" placement="top">
                          <IconButton
                            icon={<Icon as={MdThumbDown} />}
                            size="xs" variant="ghost" colorScheme="red"
                            aria-label="Reprovar"
                            onClick={() => abrirOS(os)}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Card>

      {/* Modal de Análise ── */}
      <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Flex align="center" gap="8px">
              <Icon as={MdVerifiedUser} color="brand.500" />
              Analisar OS&nbsp;
              <Text as="span" fontSize="sm" fontWeight="normal" color={subText}>
                {selectedOS?.numeroOS || selectedOS?.id?.slice(-6)}
              </Text>
              {selectedOS && (
                <Badge ml="2" colorScheme={STATUS_COLORS[selectedOS.status] || 'gray'} fontSize="10px" borderRadius="full" px="2">
                  {STATUS_LABELS[selectedOS.status] || selectedOS.status}
                </Badge>
              )}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb="4">
            {selectedOS && (() => {
              const _tipo      = selectedOS.tipoServico || '';
              const isFumaca   = ['Teste de Fumaça', 'Inspeção de Rede'].some(t => _tipo.includes(t));
              const isCorante  = _tipo.includes('Corante') || _tipo.includes('Ramal');
              const hasDadosEsp = (isFumaca || isCorante) && !!selectedOS.dadosEspecificos;
              return (
                <Tabs variant="soft-rounded" colorScheme="brand" size="sm">
                  <TabList mb="14px" flexWrap="wrap" gap="4px">
                    <Tab><Icon as={MdAssignment} mr="5px" />Detalhes</Tab>
                    {hasDadosEsp && <Tab>🔬 Dados Técnicos</Tab>}
                  </TabList>

                  <TabPanels>
                    {/* Tab 1 — Detalhes e validação ── */}
                    <TabPanel px="0">
                      <VStack align="stretch" spacing="4">

                        {/* Identificação */}
                        <Box>
                          <Text fontSize="11px" fontWeight="700" color={subText} textTransform="uppercase" mb="2">Identificação</Text>
                          <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(3, 1fr)' }} gap="12px">
                            <Campo label="Nº OS"        value={selectedOS.numeroOS} />
                            <Campo label="Protocolo"    value={selectedOS.protocolo} />
                            <Campo label="Data Serviço" value={fmtDate(selectedOS.criadaEm)} />
                            <Campo label="Técnico"      value={tecnicoNome(selectedOS)} />
                            <Campo label="Tipo Serviço" value={selectedOS.tipoServico} span={2} />
                          </Grid>
                        </Box>

                        <Divider />

                        {/* Localização */}
                        <Box>
                          <Text fontSize="11px" fontWeight="700" color={subText} textTransform="uppercase" mb="2">Localização</Text>
                          <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(3, 1fr)' }} gap="12px">
                            <Campo label="CEP"      value={de.cep} />
                            <Campo label="Cidade"   value={de.cidade || selectedOS.cidade} />
                            <Campo label="Estado"   value={de.estado} />
                            <Campo label="Endereço" value={de.endereco} span={2} />
                            <Campo label="Bairro"   value={de.bairro} />
                            {selectedOS.lat && (
                              <Campo span={3} label="Coordenadas GPS"
                                value={`${Number(selectedOS.lat).toFixed(6)}, ${Number(selectedOS.lng).toFixed(6)}`} />
                            )}
                          </Grid>
                        </Box>

                        {/* Resumo Inspeção de Rede */}
                        {de.singularidades?.length > 0 && (
                          <>
                            <Divider />
                            <Box>
                              <Text fontSize="11px" fontWeight="700" color={subText} textTransform="uppercase" mb="2">Resumo — Inspeção de Rede</Text>
                              <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap="12px">
                                <Campo label="Total Singularidades"     value={de.singularidades.length} />
                                <Campo label="Ocorrências"   value={de.ocorrencias?.length ?? 0} />
                                <Campo label="Mapa de campo" value={de.mapaCampo ? 'Sim' : 'Não'} />
                              </Grid>
                            </Box>
                          </>
                        )}

                        {/* Imóvel / Cliente (Corante) */}
                        {de.nomeCliente && (
                          <>
                            <Divider />
                            <Box>
                              <Text fontSize="11px" fontWeight="700" color={subText} textTransform="uppercase" mb="2">Dados do Imóvel / Cliente</Text>
                              <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(3, 1fr)' }} gap="12px">
                                <Campo label="Nome Cliente" value={de.nomeCliente} span={2} />
                                <Campo label="Telefone"     value={de.telefone} />
                                <Campo label="RGI"          value={de.rgi} />
                                <Campo label="Hidrômetro"   value={de.hidrometro} />
                                <Campo label="Moradores"    value={de.moradores} />
                              </Grid>
                            </Box>
                          </>
                        )}

                        {/* Observações do técnico */}
                        {selectedOS.observacoes && (
                          <>
                            <Divider />
                            <Box>
                              <Text fontSize="11px" fontWeight="700" color={subText} textTransform="uppercase" mb="2">Observações do Técnico</Text>
                              <Text fontSize="13px">{selectedOS.observacoes}</Text>
                            </Box>
                          </>
                        )}

                        {/* Comentário do Validador */}
                        <Divider />
                        <Box>
                          <Text fontSize="11px" fontWeight="700" color={subText} textTransform="uppercase" mb="2">
                            Comentário do Validador <Text as="span" fontWeight="400">(opcional)</Text>
                          </Text>
                          <Textarea
                            value={comentario}
                            onChange={(e) => setComentario(e.target.value)}
                            placeholder="Motivo da reprovação, observação para o técnico..."
                            size="sm" rows={3} borderRadius="8px"
                          />
                        </Box>

                      </VStack>
                    </TabPanel>

                    {/* Tab 2 — Dados Técnicos (somente leitura) ── */}
                    {hasDadosEsp && (
                      <TabPanel px="0" pb="0">
                        <Box
                          pointerEvents="none"
                          opacity="0.85"
                          position="relative"
                        >
                          <Box
                            position="absolute" top="0" left="0" right="0" bottom="0"
                            zIndex="1" cursor="not-allowed"
                          />
                          {isFumaca
                            ? <TesteDeFumacaForm
                                value={selectedOS.dadosEspecificos}
                                onChange={() => {}}
                              />
                            : <TesteCoranteForm
                                value={selectedOS.dadosEspecificos}
                                onChange={() => {}}
                              />
                          }
                        </Box>
                        <Text fontSize="11px" color={subText} textAlign="center" mt="2" mb="2">
                          🔒 Somente leitura — edição disponível no Painel Admin
                        </Text>
                      </TabPanel>
                    )}
                  </TabPanels>
                </Tabs>
              );
            })()}
          </ModalBody>

          <ModalFooter gap="8px">
            <Button variant="ghost" onClick={onClose} isDisabled={saving}>Cancelar</Button>
            <Button
              colorScheme="red"
              leftIcon={<Icon as={MdThumbDown} />}
              onClick={() => validar('reprovada')}
              isLoading={saving}
            >
              Reprovar
            </Button>
            <Button
              colorScheme="green"
              leftIcon={<Icon as={MdThumbUp} />}
              onClick={() => validar('aprovada')}
              isLoading={saving}
            >
              Aprovar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );

  // ── aprovação rápida sem abrir modal ────────────────────────────────────────
  async function validarDireto(os, novoStatus) {
    try {
      await updateDoc(doc(db, 'ordens', os.id), {
        status:       novoStatus,
        validadoPor:  user?.uid || null,
        validadoNome: user?.displayName || user?.email || null,
        validadoEm:   serverTimestamp(),
        atualizadaEm: serverTimestamp(),
      });
      toast({
        title: '✅ OS Aprovada!',
        description: `OS ${os.numeroOS || os.id.slice(-6)} foi aprovada.`,
        status: 'success', duration: 3000,
      });
    } catch (e) {
      toast({ title: 'Erro ao aprovar OS', description: e.message, status: 'error', duration: 4000 });
    }
  }
}

